import { useCallback, useEffect, useMemo } from 'react';
import { beginFeedback } from './api/feedback';
import type { Source } from './api/types';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { useRecommendations } from './hooks/useRecommendations';
import { useWrapped } from './hooks/useWrapped';
import { Gate } from './tsugi/Gate';
import { Hub } from './tsugi/Hub';
import { Intro } from './tsugi/intro';
import { buildNetwork, lineAffinity, type StationStop } from './tsugi/lines';
import { Station } from './tsugi/Station';
import { useTransit } from './tsugi/useTransit';
import './tsugi/styles/shell.css';
import './tsugi/styles/brand.css';
import './tsugi/styles/hub.css';
import './tsugi/styles/lines.css';
import './tsugi/styles/gate.css';
import './tsugi/styles/scenes-v2.css';
import './tsugi/styles/intro.css';
// Last: adjustments the generated sheets need for real data and real windows.
import './tsugi/styles/live.css';

/**
 * A limit of 10 is enough for a ranked list and nowhere near enough for a
 * network: nine lines want spreading across, and a rider who lands on a
 * platform holding one ticket has not really arrived anywhere. 50 is the
 * contract's ceiling (CONTRACT.md §1).
 */
const LIMIT = 50;

type View =
  | { kind: 'gate' }
  | { kind: 'intro' }
  | { kind: 'hub' }
  | { kind: 'station'; slug: string };

/**
 * How long results wait at the gate for the intro's numbers. They are computed
 * from the same list the results were, so this is a safety net for a slow
 * metadata lookup, not an expected delay: the intro is a bonus, and the
 * recommendations must never be held up by it.
 */
const INTRO_PATIENCE_MS = 6000;

/** The address to share with people who may not have a list: its gate offers the demo profile. */
function onDemoPath(): boolean {
  return window.location.pathname.replace(/\/+$/, '').toLowerCase() === '/demo';
}

export default function App() {
  const { state, submit: submitList, reset: resetList } = useRecommendations(LIMIT);
  const wrapped = useWrapped();
  const submit = useCallback(
    (username: string, source: Source) => {
      // Both read the same list; the backend's list cache makes it one upstream fetch.
      submitList(username, source);
      wrapped.load(username, source);
    },
    [submitList, wrapped.load],
  );
  const reset = useCallback(() => {
    resetList();
    wrapped.reset();
  }, [resetList, wrapped.reset]);
  const reduced = usePrefersReducedMotion();
  const transit = useTransit<View>({ kind: 'gate' });
  const { view, jump, travel } = transit;

  // The rider's lines run in the order their own list leans: what they watch
  // most comes first. Until (or unless) the intro's numbers are in, the order
  // follows their strongest recommendations instead.
  const home = wrapped.state.status === 'ready' ? wrapped.state.data.home : null;
  const network = useMemo(
    () =>
      state.status === 'success'
        ? buildNetwork(
            state.data.recommendations,
            lineAffinity(home ? (home.breakdown ?? [{ genre: home.genre, share: home.share }]) : []),
          )
        : [],
    [state, home],
  );

  // Feedback is reported against the answer currently on screen.
  useEffect(() => {
    beginFeedback(state.status === 'success' ? state.data.request_id : null);
  }, [state]);

  // Results arriving is the one move the rider did not ask for by pressing a
  // travel control, so it lands directly rather than mid-journey: at the first
  // stop of the intro when its numbers are in, at the hub when they are not.
  const waitingForIntro = state.status === 'success' && wrapped.state.status === 'loading';
  useEffect(() => {
    if (state.status === 'success' && view.kind === 'gate') {
      if (wrapped.state.status === 'ready') jump({ kind: 'intro' });
      else if (!waitingForIntro) jump({ kind: 'hub' });
    }
    if (state.status === 'idle' && view.kind !== 'gate') jump({ kind: 'gate' });
  }, [state.status, view.kind, wrapped.state.status, waitingForIntro, jump]);

  useEffect(() => {
    if (!waitingForIntro || view.kind !== 'gate') return;
    const t = window.setTimeout(() => jump({ kind: 'hub' }), INTRO_PATIENCE_MS);
    return () => window.clearTimeout(t);
  }, [waitingForIntro, view.kind, jump]);

  const current =
    view.kind === 'station' ? network.find((s) => s.line.slug === view.slug) ?? null : null;
  const index = current ? network.indexOf(current) : -1;
  const firstPick = network[0]?.picks[0] ?? null;

  const ride = (stop: StationStop, dir: 'fwd' | 'back' = 'fwd') =>
    travel({ kind: 'station', slug: stop.line.slug }, dir, {
      kanji: stop.line.kanji,
      en: stop.line.lineEn,
    });

  const toHub = () => travel({ kind: 'hub' }, 'fwd', { kanji: '中央駅', en: 'CENTRAL STATION' });

  const mode =
    view.kind === 'station' && current
      ? `station line-${current.line.slug}`
      : view.kind === 'hub'
        ? 'hub'
        : view.kind === 'intro'
          ? 'hub intro'
          : 'hub gate';

  return (
    <div className={`tsugi ${mode} ${transit.classes}`.trim()}>
      {!reduced && (
        <>
          <Streaks />
          <div className="wash" aria-hidden="true" />
          <div className="nextcard" aria-hidden="true">
            <div className="lbl">次は ・ NEXT STOP</div>
            <div className="kj">{transit.card?.kanji ?? ''}</div>
            <div className="en">{transit.card?.en ?? ''}</div>
          </div>
        </>
      )}

      {view.kind === 'gate' && (
        <Gate
          onSubmit={(username: string, source: Source) => submit(username, source)}
          onClear={reset}
          offerDemo={onDemoPath()}
          busy={state.status === 'loading' || waitingForIntro}
          queue={state.status === 'loading' ? state.queue : null}
          error={state.status === 'error' ? state.error : null}
          initialUsername={state.status === 'idle' ? '' : state.username}
          initialSource={state.status === 'idle' ? 'mal' : state.source}
        />
      )}

      {view.kind === 'intro' && wrapped.state.status === 'ready' && (
        <Intro
          data={wrapped.state.data}
          lines={network.map((s) => s.line)}
          next={firstPick ? { id: firstPick.media_id, title: firstPick.title_english ?? firstPick.title_romaji } : null}
          // The intro runs straight on into platform 01; the central station is
          // only where it goes when the rider asks for it.
          onFinish={(to) => (to === 'line' && network[0] ? ride(network[0]) : toHub())}
        />
      )}

      {view.kind === 'hub' && state.status === 'success' && (
        <Hub
          network={network}
          profile={state.data.profile}
          total={state.data.recommendations.length}
          onRide={(stop) => ride(stop)}
          onLeave={reset}
        />
      )}

      {view.kind === 'station' && current && (
        <Station
          key={current.line.slug}
          stop={current}
          network={network}
          index={index}
          onTravel={(to, dir) => ride(to, dir)}
          onMap={() => travel({ kind: 'hub' }, 'back', { kanji: '路線図', en: 'NETWORK MAP' })}
          source={state.status === 'success' ? state.data.profile.source : 'anilist'}
        />
      )}
    </div>
  );
}

/** Window streaks, seeded once — they rush past during a departure. */
function Streaks() {
  const bars = useMemo(
    () =>
      Array.from({ length: 26 }, () => ({
        top: `${(Math.random() * 100).toFixed(1)}vh`,
        width: `${(10 + Math.random() * 38).toFixed(0)}vw`,
        opacity: Number((0.2 + Math.random() * 0.7).toFixed(2)),
        animationDuration: `${(0.28 + Math.random() * 0.85).toFixed(2)}s`,
        animationDelay: `-${(Math.random() * 2).toFixed(2)}s`,
      })),
    [],
  );
  return (
    <div className="streaks" aria-hidden="true">
      {bars.map((style, i) => (
        <i key={i} style={style} />
      ))}
    </div>
  );
}
